import { useI18n } from '@/hooks/useI18n';
import { Layers } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PathLabel } from '@/components/primitives/PathLabel';
import { TEMPLATE_PERIOD_OPTIONS, TEMPLATE_SORT_OPTIONS } from '@/lib/constants';
import type { IdentityResponse, TemplateSortBy } from '@/types/api';

interface TemplatesHeaderProps {
  periodDays: number;
  onPeriodChange: (days: number) => void;
  sortBy: TemplateSortBy;
  onSortByChange: (s: TemplateSortBy) => void;
  identityFilter: string | 'all';
  onIdentityChange: (id: string | 'all') => void;
  identities: IdentityResponse[] | undefined;
  totalTemplates: number;
}

export function TemplatesHeader({
  periodDays,
  onPeriodChange,
  sortBy,
  onSortByChange,
  identityFilter,
  onIdentityChange,
  identities,
  totalTemplates,
}: TemplatesHeaderProps) {
  const { t } = useI18n();
  return (
    <header className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <PathLabel
            path="/templates"
            meta={
              totalTemplates > 0
                ? t(`${totalTemplates} détectés`, `${totalTemplates} detected`)
                : undefined
            }
          />
          <h1 className="mt-1 flex items-center gap-2 text-[22px] font-semibold tracking-tight text-text-1">
            <Layers className="h-5 w-5 text-violet-deep" />
            {t('Templates de reels', 'Reel templates')}
          </h1>
          <p className="text-[13px] text-text-3">
            {t(
              'Reels groupés par caption identique — identifie les formats qui percent le mieux.',
              'Reels grouped by matching captions — find the formats that perform best.',
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 items-end gap-3 min-[380px]:grid-cols-2 sm:flex sm:flex-wrap">
        <LabeledControl label={t('Période', 'Period')}>
          <Select value={String(periodDays)} onValueChange={(v) => onPeriodChange(Number(v))}>
            <SelectTrigger className="h-11 w-full sm:h-9 sm:w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEMPLATE_PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {t(opt.label, opt.labelEn)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </LabeledControl>

        <LabeledControl label={t('Trier par', 'Sort by')}>
          <Select value={sortBy} onValueChange={(v) => onSortByChange(v as TemplateSortBy)}>
            <SelectTrigger className="h-11 w-full sm:h-9 sm:w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEMPLATE_SORT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {t(opt.label, opt.labelEn)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </LabeledControl>

        <LabeledControl label={t('Identité', 'Identity')}>
          <Select value={identityFilter} onValueChange={onIdentityChange}>
            <SelectTrigger className="h-11 w-full sm:h-9 sm:w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('Toutes', 'All')}</SelectItem>
              {(identities ?? []).map((ident) => (
                <SelectItem key={ident.id} value={ident.id}>
                  {ident.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </LabeledControl>
      </div>
    </header>
  );
}

function LabeledControl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-text-4">
        {label}
      </span>
      {children}
    </div>
  );
}
